import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2, Edit3, Plus, Users, DollarSign, Loader2 } from 'lucide-react';
import { useRoles, useCreateRole, useUpdateRole, useDeleteRole, useRolesWithEmployeeCount } from '@/hooks/employees/useRoles';
import { useEmploymentTypes } from '@/hooks/compliance/useEmploymentTypes';
import { Role } from '@/types';
import { toast } from '@/hooks/ui/use-toast';
import { DotyposService } from '@/lib/integrations/dotypos-service';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface RoleManagementTabProps {
  businessId: string;
}

interface RoleFormData {
  name: string;
  description: string;
  default_pay_rate: number | null;
  employment_type_id: string | null;
  hours_per_month: number | null;
  dotypos_access_level: number | null;
}

// Dotypos POS access level options
const DOTYPOS_ACCESS_LEVELS = [
  { value: 0, label: 'No Access', description: 'Cannot use POS' },
  { value: 15, label: 'Basic Access', description: 'Sales operations only' },
  { value: 255, label: 'Full Access', description: 'All POS features' },
];

const RoleManagementTab: React.FC<RoleManagementTabProps> = ({ businessId }) => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
  const [formData, setFormData] = useState<RoleFormData>({
    name: '',
    description: '',
    default_pay_rate: null,
    employment_type_id: null,
    hours_per_month: null,
    dotypos_access_level: 0,
  });

  const { data: roles, isLoading, error, refetch } = useRolesWithEmployeeCount(businessId);
  const { employmentTypes, isLoading: loadingEmploymentTypes } = useEmploymentTypes(businessId);
  const createRoleMutation = useCreateRole();
  const updateRoleMutation = useUpdateRole();
  const deleteRoleMutation = useDeleteRole();

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      default_pay_rate: null,
      employment_type_id: null,
      hours_per_month: null,
      dotypos_access_level: 0,
    });
  };

  // Derive pay_type from employment type category
  const getPayTypeFromEmploymentType = (employmentTypeId: string | null): string => {
    if (!employmentTypeId) return 'hourly';
    const empType = employmentTypes.find(et => et.id === employmentTypeId);
    // employee category = salary, agreement category = hourly
    return empType?.category === 'employee' ? 'salary' : 'hourly';
  };

  const handleCreateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Role name is required');
      return;
    }

    try {
      const payType = getPayTypeFromEmploymentType(formData.employment_type_id);

      await createRoleMutation.mutateAsync({
        business_id: businessId,
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        default_pay_rate: formData.default_pay_rate,
        pay_type: payType,
        employment_type_id: formData.employment_type_id,
        hours_per_month: formData.hours_per_month,
        dotypos_access_level: formData.dotypos_access_level,
      });

      toast.success('Role created successfully');
      await refetch();

      resetForm();
      setIsCreateDialogOpen(false);
    } catch (error) {
      toast.error('Failed to create role');
    }
  };

  const handleUpdateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingRole || !formData.name.trim()) {
      toast.error('Role name is required');
      return;
    }

    try {
      const payType = getPayTypeFromEmploymentType(formData.employment_type_id);
      const accessLevelChanged = editingRole.dotypos_access_level !== formData.dotypos_access_level;

      await updateRoleMutation.mutateAsync({
        id: editingRole.id,
        updates: {
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          default_pay_rate: formData.default_pay_rate,
          pay_type: payType,
          employment_type_id: formData.employment_type_id,
          hours_per_month: formData.hours_per_month,
          dotypos_access_level: formData.dotypos_access_level,
        },
      });

      toast.success('Role updated successfully');

      // If POS access level changed, sync to Dotypos
      if (accessLevelChanged && formData.dotypos_access_level !== null) {
        try {
          const syncResult = await DotyposService.syncRoleAccessLevel(
            businessId,
            editingRole.id,
            formData.dotypos_access_level
          );
          if (syncResult.updated > 0) {
            toast.success(`POS access synced for ${syncResult.updated} employee(s)`);
          }
          if (syncResult.failed > 0) {
            toast.error(`Failed to sync ${syncResult.failed} employee(s) to POS`);
          }
        } catch (syncError) {
          // Don't fail the role update if sync fails, just show a warning
          console.error('Failed to sync role access level to Dotypos:', syncError);
          toast.error('Role updated, but failed to sync POS access levels');
        }
      }

      await refetch();

      resetForm();
      setEditingRole(null);
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleDeleteRole = async () => {
    if (!roleToDelete) return;

    try {
      await deleteRoleMutation.mutateAsync({
        id: roleToDelete.id,
        businessId: businessId,
      });

      toast.success('Role deleted successfully');
      await refetch();

      setRoleToDelete(null);
    } catch (error) {
      toast.error('Failed to delete role');
    }
  };

  const startEditing = (role: Role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      description: role.description || '',
      default_pay_rate: role.default_pay_rate,
      employment_type_id: role.employment_type_id || null,
      hours_per_month: role.hours_per_month,
      dotypos_access_level: role.dotypos_access_level ?? 0,
    });
  };

  const cancelEditing = () => {
    setEditingRole(null);
    resetForm();
  };

  const openCreateDialog = () => {
    resetForm();
    setIsCreateDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-lg">Loading roles...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-lg text-red-500">Error loading roles</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-sm font-medium">Role Management</CardTitle>
            <Button onClick={openCreateDialog} className="flex items-center gap-2">
              <Plus size={16} />
              Add Role
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {roles?.map((role) => (
              <Card key={role.id} className="overflow-hidden border hover:shadow-lg transition-shadow duration-200 h-fit">
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-primary/10 dark:bg-primary/20 rounded-lg text-primary">
                        <Users className="h-4 w-4" />
                      </div>
                      <div>
                        <CardTitle className="text-base font-semibold">{role.name}</CardTitle>
                        <CardDescription className="text-xs">
                          {role.employee_count} employees • {role.employmentType?.label_native || 'No type set'}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => startEditing(role)}
                        className="h-8 w-8 p-0 text-muted-foreground hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 border hover:border-blue-400"
                        title="Edit role"
                      >
                        <Edit3 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setRoleToDelete(role)}
                        className="h-8 w-8 p-0 text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 border hover:border-red-400"
                        title="Delete role"
                        disabled={role.employee_count > 0}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-2">
                    {role.description && (
                      <p className="text-xs text-muted-foreground">{role.description}</p>
                    )}
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <DollarSign className="h-3 w-3" />
                      {role.default_pay_rate
                        ? `€${role.default_pay_rate.toFixed(2)}${role.employmentType?.category === 'agreement' ? '/hour' : '/month'}`
                        : 'No default rate'
                      }
                      {role.employmentType?.category === 'employee' && role.hours_per_month && (
                        <span>({role.hours_per_month}h/month)</span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {roles?.length === 0 && (
              <Card
                className="overflow-hidden border-2 border-dashed border-muted-foreground/30 hover:border-primary hover:shadow-lg transition-all duration-200 h-fit cursor-pointer bg-muted/30"
                onClick={openCreateDialog}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-muted rounded-lg">
                      <Plus className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div>
                      <CardTitle className="text-base font-semibold text-muted-foreground">Create Your First Role</CardTitle>
                      <CardDescription className="text-xs">
                        Click to add a new role
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-xs text-muted-foreground">
                    Roles help you organize employees and set default pay rates
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create Role Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Create New Role</DialogTitle>
            <DialogDescription>
              Create a new role for your employees with specific pay rates and responsibilities.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateRole} className="space-y-4">
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="name" className="text-right">
                  Name
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="col-span-3"
                  placeholder="e.g., Manager, Server, Chef"
                  required
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="description" className="text-right">
                  Description
                </Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="col-span-3"
                  placeholder="Optional description of the role"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="employment_type" className="text-right">
                  Contract Type
                </Label>
                <Select
                  value={formData.employment_type_id || ''}
                  onValueChange={(value) => setFormData({ ...formData, employment_type_id: value || null })}
                  disabled={loadingEmploymentTypes}
                >
                  <SelectTrigger className="col-span-3">
                    {loadingEmploymentTypes ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Loading...</span>
                      </div>
                    ) : (
                      <SelectValue placeholder="Select contract type" />
                    )}
                  </SelectTrigger>
                  <SelectContent>
                    {employmentTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id} hideIndicator>
                        <div className="flex flex-col">
                          <span>{type.label_native}</span>
                          <span className="text-xs text-muted-foreground">{type.label_en}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="pay_rate" className="text-right">
                  Default Rate
                </Label>
                <Input
                  id="pay_rate"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.default_pay_rate || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    default_pay_rate: e.target.value ? parseFloat(e.target.value) : null
                  })}
                  className="col-span-3"
                  placeholder={
                    employmentTypes.find(et => et.id === formData.employment_type_id)?.category === 'agreement'
                      ? '8.50 (per hour)'
                      : '1200 (per month)'
                  }
                />
              </div>
              {employmentTypes.find(et => et.id === formData.employment_type_id)?.category === 'employee' && (
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="hours_per_month" className="text-right">
                    Hours/Month
                  </Label>
                  <Input
                    id="hours_per_month"
                    type="number"
                    min="1"
                    max="744"
                    value={formData.hours_per_month || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      hours_per_month: e.target.value ? parseInt(e.target.value) : null
                    })}
                    className="col-span-3"
                    placeholder="160"
                  />
                </div>
              )}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="dotypos_access" className="text-right">
                  POS Access
                </Label>
                <Select
                  value={String(formData.dotypos_access_level ?? 0)}
                  onValueChange={(value) => setFormData({ ...formData, dotypos_access_level: parseInt(value) })}
                >
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Select POS access level" />
                  </SelectTrigger>
                  <SelectContent>
                    {DOTYPOS_ACCESS_LEVELS.map((level) => (
                      <SelectItem key={level.value} value={String(level.value)} hideIndicator>
                        <div className="flex flex-col">
                          <span>{level.label}</span>
                          <span className="text-xs text-muted-foreground">{level.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createRoleMutation.isPending}>
                {createRoleMutation.isPending ? 'Creating...' : 'Create Role'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={!!editingRole} onOpenChange={() => setEditingRole(null)}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit Role</DialogTitle>
            <DialogDescription>
              Modify the role details, pay rates, and responsibilities.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateRole} className="space-y-4">
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-name" className="text-right">
                  Name
                </Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="col-span-3"
                  placeholder="e.g., Manager, Server, Chef"
                  required
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-description" className="text-right">
                  Description
                </Label>
                <Textarea
                  id="edit-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="col-span-3"
                  placeholder="Optional description of the role"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-employment_type" className="text-right">
                  Contract Type
                </Label>
                <Select
                  value={formData.employment_type_id || ''}
                  onValueChange={(value) => setFormData({ ...formData, employment_type_id: value || null })}
                  disabled={loadingEmploymentTypes}
                >
                  <SelectTrigger className="col-span-3">
                    {loadingEmploymentTypes ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Loading...</span>
                      </div>
                    ) : (
                      <SelectValue placeholder="Select contract type" />
                    )}
                  </SelectTrigger>
                  <SelectContent>
                    {employmentTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id} hideIndicator>
                        <div className="flex flex-col">
                          <span>{type.label_native}</span>
                          <span className="text-xs text-muted-foreground">{type.label_en}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-pay_rate" className="text-right">
                  Default Rate
                </Label>
                <Input
                  id="edit-pay_rate"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.default_pay_rate || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    default_pay_rate: e.target.value ? parseFloat(e.target.value) : null
                  })}
                  className="col-span-3"
                  placeholder={
                    employmentTypes.find(et => et.id === formData.employment_type_id)?.category === 'agreement'
                      ? '8.50 (per hour)'
                      : '1200 (per month)'
                  }
                />
              </div>
              {employmentTypes.find(et => et.id === formData.employment_type_id)?.category === 'employee' && (
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="edit-hours_per_month" className="text-right">
                    Hours/Month
                  </Label>
                  <Input
                    id="edit-hours_per_month"
                    type="number"
                    min="1"
                    max="744"
                    value={formData.hours_per_month || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      hours_per_month: e.target.value ? parseInt(e.target.value) : null
                    })}
                    className="col-span-3"
                    placeholder="160"
                  />
                </div>
              )}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-dotypos_access" className="text-right">
                  POS Access
                </Label>
                <Select
                  value={String(formData.dotypos_access_level ?? 0)}
                  onValueChange={(value) => setFormData({ ...formData, dotypos_access_level: parseInt(value) })}
                >
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Select POS access level" />
                  </SelectTrigger>
                  <SelectContent>
                    {DOTYPOS_ACCESS_LEVELS.map((level) => (
                      <SelectItem key={level.value} value={String(level.value)} hideIndicator>
                        <div className="flex flex-col">
                          <span>{level.label}</span>
                          <span className="text-xs text-muted-foreground">{level.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={cancelEditing}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateRoleMutation.isPending}>
                {updateRoleMutation.isPending ? 'Updating...' : 'Update Role'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!roleToDelete} onOpenChange={() => setRoleToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the role "{roleToDelete?.name}".
              {roleToDelete?.employee_count && roleToDelete.employee_count > 0 && (
                <span className="text-red-600 font-medium">
                  {' '}This role is currently assigned to {roleToDelete.employee_count} employee(s) and cannot be deleted.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteRole}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteRoleMutation.isPending || (roleToDelete?.employee_count ?? 0) > 0}
            >
              {deleteRoleMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default RoleManagementTab;